import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircleIcon, ExclamationTriangleIcon, LockClosedIcon, CameraIcon, DocumentIcon,
} from '@heroicons/react/24/outline';
import onboardingService from '../../services/onboardingService';
import { parseSaId, initialsOf } from '../../utils/saId';
import Logo from '../../components/Logo';

const TITLES = ['', 'Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Prof', 'Master'];
const RELATIONSHIPS = [
  ['main_member', 'I am the main member'],
  ['spouse', 'Spouse / partner of the main member'],
  ['child', 'Child of the main member'],
  ['other', 'Other dependant'],
];

const REFUSED = {
  invalid: ['This link isn’t valid', 'Please check that you opened the complete link from your email.'],
  expired: ['This link has expired', 'Links work for 7 days. Please ask the practice to send you a new one.'],
  used: ['Your details have already been sent', 'Thank you — the practice has received your details and will contact you if anything else is needed.'],
  cancelled: ['This link is no longer active', 'Please contact the practice for a new link.'],
  no_practice: ['This page isn’t linked to a practice', 'Please open the link exactly as it appears in your email.'],
};

// Photos are shrunk on the phone before upload (a phone photo can be 5 MB+); PDFs are sent as they are.
const MAX_PDF = 3 * 1024 * 1024;
const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(new Error('That file could not be read.'));
  r.readAsDataURL(file);
});
const prepareFile = async (file) => {
  if (file.type === 'application/pdf') {
    if (file.size > MAX_PDF) throw new Error('That PDF is too large — please keep it under 3 MB, or take a photo instead.');
    return { data: await readAsDataUrl(file), mime: 'application/pdf', name: file.name };
  }
  if (!/^image\//.test(file.type)) throw new Error('Please choose a photo or a PDF.');
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('That photo could not be read — please try another.'));
      i.src = url;
    });
    const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    return { data: canvas.toDataURL('image/jpeg', 0.82), mime: 'image/jpeg', name: `${file.name.replace(/\.[^.]+$/, '')}.jpg` };
  } finally {
    URL.revokeObjectURL(url);
  }
};

/**
 * PublicOnboarding — the page a patient opens from the practice's email
 * (/onboard/:token on the practice's own web address). No login: the link code
 * is the key, it works once, and it expires. What the patient sends is reviewed
 * by the practice before it's used.
 */
const PublicOnboarding = () => {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading'); // loading | form | refused | done
  const [data, setData] = useState(null);
  const [refused, setRefused] = useState(null);
  const [f, setF] = useState(null);
  const [docs, setDocs] = useState({ id_document: null, medical_aid_card: null });
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const errorsRef = useRef(null);

  useEffect(() => {
    onboardingService.publicLoad(token)
      .then((d) => {
        setData(d);
        setF({
          title: '', first_names: d.invite.first_names, surname: d.invite.surname, id_type: 'sa_id', id_number: '',
          date_of_birth: '', sex: '', cell_phone: d.invite.cell_phone || '', email: d.invite.email,
          street_address: '', suburb: '', city: '', postal_code: '', payer: 'medical_aid',
          scheme_id: '', plan_option: '', membership_number: '', dependant_code: '00', relationship: 'main_member',
          mm_title: '', mm_first_names: '', mm_surname: '', mm_id_number: '',
        });
        setPhase('form');
      })
      .catch((e) => {
        const d = e.response?.data;
        setRefused({ code: d?.code || 'invalid', practice: d?.practice, network: !e.response });
        setPhase('refused');
      });
  }, [token]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const sa = f && f.id_type === 'sa_id' && f.id_number.replace(/\s/g, '').length >= 13 ? parseSaId(f.id_number) : null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors([]);
    const payload = {
      patient: {
        title: f.title, first_names: f.first_names, surname: f.surname, id_type: f.id_type,
        id_number: f.id_type === 'none' ? null : f.id_number,
        date_of_birth: sa?.valid ? sa.dateOfBirth : f.date_of_birth, sex: sa?.valid ? sa.sex : f.sex,
        cell_phone: f.cell_phone, email: f.email, street_address: f.street_address, suburb: f.suburb, city: f.city,
        postal_code: f.postal_code, payer: f.payer,
      },
      cover: f.payer === 'medical_aid' ? {
        scheme_id: f.scheme_id, plan_option: f.plan_option, membership_number: f.membership_number,
        dependant_code: f.dependant_code, relationship: f.relationship,
        main_member: f.relationship === 'main_member' ? undefined : {
          title: f.mm_title, first_names: f.mm_first_names, surname: f.mm_surname, initials: initialsOf(f.mm_first_names), id_number: f.mm_id_number,
        },
      } : null,
      documents: {
        id_document: docs.id_document,
        medical_aid_card: f.payer === 'medical_aid' ? docs.medical_aid_card : null,
      },
      consent,
    };
    try {
      await onboardingService.publicSubmit(token, payload);
      setPhase('done');
      window.scrollTo({ top: 0 });
    } catch (ex) {
      const d = ex.response?.data;
      if (d?.code && REFUSED[d.code]) {
        setRefused({ code: d.code, practice: data.practice });
        setPhase('refused');
      } else {
        setErrors(d?.errors || [d?.err || (ex.response ? 'Something went wrong — please try again.' : 'You seem to be offline — check your connection and try again.')]);
        setTimeout(() => errorsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      }
    } finally {
      setSaving(false);
    }
  };

  const practice = data?.practice || refused?.practice || {};
  const contact = [practice.phone, practice.email].filter(Boolean).join(' · ');

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-lg font-bold text-brand-deep">{practice.name || 'Patient details'}</p>
            {contact && <p className="truncate text-xs text-slate-500">{contact}</p>}
          </div>
          <span className="flex shrink-0 items-center gap-1 text-[11px] text-slate-400"><LockClosedIcon className="h-3.5 w-3.5" /> Secure form</span>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6">
        {phase === 'loading' && <p className="py-16 text-center text-slate-400">Loading…</p>}

        {phase === 'refused' && (
          <Card>
            <div className="flex flex-col items-center py-6 text-center">
              {refused.code === 'used' ? <CheckCircleIcon className="h-12 w-12 text-green-600" /> : <ExclamationTriangleIcon className="h-12 w-12 text-amber-500" />}
              <h1 className="mt-3 text-xl font-bold text-brand-deep">{refused.network ? 'We couldn’t reach the practice' : REFUSED[refused.code]?.[0]}</h1>
              <p className="mt-2 max-w-md text-sm text-slate-600">{refused.network ? 'Please check your internet connection and open the link again.' : REFUSED[refused.code]?.[1]}</p>
              {contact && refused.code !== 'used' && <p className="mt-3 text-sm font-semibold text-slate-700">{contact}</p>}
            </div>
          </Card>
        )}

        {phase === 'done' && (
          <Card>
            <div className="flex flex-col items-center py-6 text-center">
              <CheckCircleIcon className="h-12 w-12 text-green-600" />
              <h1 className="mt-3 text-xl font-bold text-brand-deep">Thank you, {f.first_names}</h1>
              <p className="mt-2 max-w-md text-sm text-slate-600">
                Your details have been sent securely to {practice.name || 'the practice'}. They’ll check them and contact you if anything else is needed. You can close this page.
              </p>
            </div>
          </Card>
        )}

        {phase === 'form' && f && (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <div>
              <h1 className="text-2xl font-bold text-brand-deep">Hi {data.invite.first_names}</h1>
              <p className="mt-1 text-sm text-slate-600">
                Please complete your details for {practice.name || 'the practice'}. It takes about five minutes — have your ID and medical aid card with you.
              </p>
            </div>

            <Card title="1. About you">
              <Grid>
                <Field label="Title" span={2}>
                  <select className={inputCls} value={f.title} onChange={(e) => set('title', e.target.value)}>
                    {TITLES.map((t) => <option key={t} value={t}>{t || '—'}</option>)}
                  </select>
                </Field>
                <Field label="First names (as on your ID) *" span={4}><input className={inputCls} value={f.first_names} onChange={(e) => set('first_names', e.target.value)} autoComplete="given-name" /></Field>
                <Field label="Surname *" span={6}><input className={inputCls} value={f.surname} onChange={(e) => set('surname', e.target.value)} autoComplete="family-name" /></Field>
                <Field label="I have a…" span={6}>
                  <div className="flex flex-wrap gap-2">
                    {[['sa_id', 'South African ID'], ['passport', 'Passport'], ['none', 'Neither']].map(([v, l]) => (
                      <Choice key={v} on={f.id_type === v} onClick={() => set('id_type', v)}>{l}</Choice>
                    ))}
                  </div>
                </Field>
                {f.id_type !== 'none' && (
                  <Field label={f.id_type === 'sa_id' ? 'ID number *' : 'Passport number *'} span={6}>
                    <input
                      className={`${inputCls} font-mono`}
                      value={f.id_number}
                      onChange={(e) => set('id_number', e.target.value)}
                      inputMode={f.id_type === 'sa_id' ? 'numeric' : 'text'}
                      maxLength={f.id_type === 'sa_id' ? 13 : 20}
                    />
                    {sa && <span className={`text-xs ${sa.valid ? 'text-green-700' : 'text-red-600'}`}>{sa.valid ? '✓ Valid ID number' : sa.error}</span>}
                  </Field>
                )}
                {f.id_type !== 'sa_id' && (
                  <>
                    <Field label="Date of birth *" span={3}><input type="date" className={inputCls} value={f.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} /></Field>
                    <Field label="Sex *" span={3}>
                      <select className={inputCls} value={f.sex} onChange={(e) => set('sex', e.target.value)}>
                        <option value="">Choose…</option>
                        <option value="F">Female</option>
                        <option value="M">Male</option>
                      </select>
                    </Field>
                  </>
                )}
              </Grid>
            </Card>

            <Card title="2. Contact details">
              <Grid>
                <Field label="Cell number" span={3}><input className={inputCls} value={f.cell_phone} onChange={(e) => set('cell_phone', e.target.value)} inputMode="tel" autoComplete="tel" /></Field>
                <Field label="Email" span={3}><input type="email" className={inputCls} value={f.email} onChange={(e) => set('email', e.target.value)} autoComplete="email" /></Field>
                <Field label="Street address" span={6}><input className={inputCls} value={f.street_address} onChange={(e) => set('street_address', e.target.value)} autoComplete="street-address" /></Field>
                <Field label="Suburb" span={2}><input className={inputCls} value={f.suburb} onChange={(e) => set('suburb', e.target.value)} /></Field>
                <Field label="City" span={2}><input className={inputCls} value={f.city} onChange={(e) => set('city', e.target.value)} autoComplete="address-level2" /></Field>
                <Field label="Postal code" span={2}><input className={inputCls} value={f.postal_code} onChange={(e) => set('postal_code', e.target.value)} inputMode="numeric" autoComplete="postal-code" /></Field>
              </Grid>
            </Card>

            <Card title="3. Medical aid">
              <div className="mb-3 flex flex-wrap gap-2">
                <Choice on={f.payer === 'medical_aid'} onClick={() => set('payer', 'medical_aid')}>I have medical aid</Choice>
                <Choice on={f.payer === 'private'} onClick={() => set('payer', 'private')}>I pay the account myself</Choice>
              </div>
              {f.payer === 'medical_aid' && (
                <Grid>
                  <Field label="Medical scheme *" span={6}>
                    <select className={inputCls} value={f.scheme_id} onChange={(e) => set('scheme_id', e.target.value)}>
                      <option value="">Choose your scheme…</option>
                      {data.schemes.map((s) => <option key={s.scheme_id} value={s.scheme_id}>{s.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Plan / option" span={3}><input className={inputCls} value={f.plan_option} onChange={(e) => set('plan_option', e.target.value)} placeholder="As on your card" /></Field>
                  <Field label="Membership number *" span={3}><input className={`${inputCls} font-mono uppercase`} value={f.membership_number} onChange={(e) => set('membership_number', e.target.value)} /></Field>
                  <Field label="Dependant code *" span={2}><input className={`${inputCls} font-mono`} value={f.dependant_code} onChange={(e) => set('dependant_code', e.target.value)} inputMode="numeric" maxLength={3} /></Field>
                  <Field label="On this medical aid, I am the…" span={4}>
                    <select className={inputCls} value={f.relationship} onChange={(e) => set('relationship', e.target.value)}>
                      {RELATIONSHIPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Field>
                  {f.relationship !== 'main_member' && (
                    <>
                      <p className="col-span-6 -mb-1 mt-1 text-xs font-bold uppercase tracking-wide text-slate-400">Main member</p>
                      <Field label="Title" span={2}>
                        <select className={inputCls} value={f.mm_title} onChange={(e) => set('mm_title', e.target.value)}>
                          {TITLES.map((t) => <option key={t} value={t}>{t || '—'}</option>)}
                        </select>
                      </Field>
                      <Field label="First names *" span={4}><input className={inputCls} value={f.mm_first_names} onChange={(e) => set('mm_first_names', e.target.value)} /></Field>
                      <Field label="Surname *" span={3}><input className={inputCls} value={f.mm_surname} onChange={(e) => set('mm_surname', e.target.value)} /></Field>
                      <Field label="ID number" span={3}><input className={`${inputCls} font-mono`} value={f.mm_id_number} onChange={(e) => set('mm_id_number', e.target.value)} inputMode="numeric" maxLength={13} /></Field>
                    </>
                  )}
                </Grid>
              )}
            </Card>

            <Card title="4. Documents">
              <div className="flex flex-col gap-3">
                <DocUpload
                  label="Your ID document"
                  hint="The photo page of your ID book, both sides of your ID card, or your passport."
                  value={docs.id_document}
                  onChange={(v) => setDocs((d) => ({ ...d, id_document: v }))}
                />
                {f.payer === 'medical_aid' && (
                  <DocUpload
                    label="Your medical aid card"
                    hint="The side showing your membership number."
                    value={docs.medical_aid_card}
                    onChange={(v) => setDocs((d) => ({ ...d, medical_aid_card: v }))}
                  />
                )}
              </div>
            </Card>

            <Card title="5. Consent">
              <p className="rounded-lg bg-slate-50 p-3 text-sm leading-relaxed text-slate-600">{data.consent_text}</p>
              <label className="mt-3 flex cursor-pointer items-start gap-3">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-5 w-5 accent-brand-navy" />
                <span className="text-sm font-semibold text-slate-700">I have read and agree to the above.</span>
              </label>
            </Card>

            {errors.length > 0 && (
              <div ref={errorsRef} className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <p className="font-semibold">Please check the following:</p>
                <ul className="mt-1 list-disc pl-5">{errors.map((er) => <li key={er}>{er}</li>)}</ul>
              </div>
            )}

            <button type="submit" disabled={saving} className="h-14 rounded-xl bg-brand-gradient text-base font-bold text-white shadow-sm hover:opacity-95 disabled:opacity-60">
              {saving ? 'Sending securely…' : 'Send my details'}
            </button>
            <p className="text-center text-xs text-slate-400">
              This link works once and expires on {new Date(data.invite.expires_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long' })}.
            </p>
          </form>
        )}

        <footer className="mt-10 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
          <Logo variant="icon" className="h-4 w-4" /> Powered by OmniPrac
        </footer>
      </main>
    </div>
  );
};

const DocUpload = ({ label, hint, value, onChange }) => {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    setError('');
    setBusy(true);
    try {
      onChange(await prepareFile(file));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-700">{label} *</p>
          <p className="text-xs text-slate-500">{hint}</p>
        </div>
        {value && <button type="button" onClick={() => onChange(null)} className="text-xs font-semibold text-slate-500 hover:text-red-600">Remove</button>}
      </div>
      {value ? (
        value.mime === 'application/pdf'
          ? <p className="mt-2 flex items-center gap-2 text-sm text-slate-700"><DocumentIcon className="h-5 w-5 text-brand-teal" />{value.name}</p>
          : <img src={value.data} alt={label} className="mt-2 max-h-52 rounded-lg border border-slate-200" />
      ) : (
        <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 px-4 py-6 text-sm font-semibold text-slate-500 hover:border-brand-teal hover:text-brand-teal">
          <CameraIcon className="h-5 w-5" /> {busy ? 'Preparing…' : 'Take a photo or choose a file'}
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        </label>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

const inputCls = 'w-full rounded-lg border border-slate-300 bg-white p-3 text-base outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40';
const SPAN = { 2: 'sm:col-span-2', 3: 'sm:col-span-3', 4: 'sm:col-span-4', 6: 'sm:col-span-6' };

const Card = ({ title, children }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
    {title && <h2 className="mb-3 text-base font-bold text-brand-deep">{title}</h2>}
    {children}
  </section>
);
const Grid = ({ children }) => <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">{children}</div>;
const Field = ({ label, span = 6, children }) => (
  <label className={`flex flex-col gap-1 ${SPAN[span]}`}>
    <span className="text-sm font-semibold text-slate-600">{label}</span>
    {children}
  </label>
);
const Choice = ({ on, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border px-4 py-2 text-sm font-semibold ${on ? 'border-brand-navy bg-brand-navy text-white' : 'border-slate-300 bg-white text-slate-600'}`}
  >
    {children}
  </button>
);

export default PublicOnboarding;
